import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { authCheckUrl } from "config";
import type { Dispatch, Middleware, UnknownAction } from "@reduxjs/toolkit";
import {
  ConnectionHealthRegistration,
  ConnectionHealthStatus,
  registerConnection,
  updateConnectionStatus,
} from "_shared/_stores/realtime/connection-health-redux-slice";
import Session from "users/__session/session";
import { HttpStatusCode } from "axios";
import { isFunction } from "lodash";

export type SignalThunk<S = unknown, D extends Dispatch = Dispatch<UnknownAction>, R = void | Promise<void>> = (
  dispatch: D,
  getState: () => S,
  invoke: HubConnection["invoke"],
) => R;

export type EventCallback<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> = (
  ...args: unknown[]
) => SignalThunk<S, D>;

export interface WithCallbacks<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> {
  add(name: string, callback: EventCallback<S, D>): WithCallbacks<S, D>;
  callbackArray: Array<{ name: string; callback: EventCallback<S, D> }>;
}

export const withCallbacks = <S = unknown, D extends Dispatch = Dispatch<UnknownAction>>(): WithCallbacks<S, D> => {
  const callbackArray = new Array<{ name: string; callback: EventCallback<S, D> }>();
  return {
    add(name: string, callback: EventCallback<S, D>) {
      callbackArray.push({ name, callback });
      return this;
    },
    callbackArray,
  };
};

export interface MiddlewareConfig<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> {
  callbacks: WithCallbacks<S, D>;
  connection: HubConnection;
  connectionHealth?: ConnectionHealthRegistration;
  onStart?: () => void;
  shouldConnectionStartImmediately?: boolean;
}

type StatusReporter = (status: ConnectionHealthStatus, error?: unknown) => void;

const isSignalThunk = (a: unknown): a is SignalThunk =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof a === "function" && (a as any).__signalThunk === true;

const startLocks = new WeakMap<HubConnection, Promise<void>>();
const lifecycleInstalled = new WeakSet<HubConnection>();

const toErrorText = (error: unknown): string | null => {
  if (!error) return null;
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;

  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown SignalR error";
  }
};

export const connect = (connection: HubConnection, onStart?: () => void, reportStatus?: StatusReporter): Promise<void> => {
  const existing = startLocks.get(connection);
  if (existing) return existing;

  const task = (async () => {
    const session = Session.loadFromStorage(() => {});
    const token = session.authToken?.value;

    if (session.authToken?.isExpired) {
      reportStatus?.("stopped");
      session.logout();
      return;
    }
    if (!token) {
      reportStatus?.("stopped");
      return;
    }

    if (connection.state === HubConnectionState.Connected)
      reportStatus?.("connected");
    if (connection.state !== HubConnectionState.Disconnected)
      return;

    try {
      reportStatus?.("connecting");
      await connection.start();
      reportStatus?.("connected");
      onStart?.();
    } catch (err) {
      const authCheckResponse = await fetch(authCheckUrl);
      if (authCheckResponse.status === HttpStatusCode.Unauthorized) {
        console.warn("SignalR connection failed due to unauthorized session. Not retrying until re-login.");
        reportStatus?.("stopped", err);
        session.logout();
        return;
      }
      reportStatus?.("disconnected", err);
      throw err;
    }
  })().finally(() => {
    startLocks.delete(connection);
  });

  startLocks.set(connection, task);
  return task;
};

const reduxSignalRMiddleware =
  <S = unknown, D extends Dispatch = Dispatch<UnknownAction>>({
    callbacks,
    onStart,
    connection,
    connectionHealth,
    shouldConnectionStartImmediately = true,
  }: MiddlewareConfig<S, D>): Middleware<object, S, D> =>
  (store) => {
    const reportStatus: StatusReporter | undefined = connectionHealth
      ? (status, error) => {
          store.dispatch(
            updateConnectionStatus({
              ...connectionHealth,
              status,
              lastError: toErrorText(error),
            }) as UnknownAction,
          );
        }
      : undefined;

    for (const { name, callback } of callbacks.callbackArray) {
      connection.on(name, (...args: unknown[]) => {
        try {
          const thunk = callback(...args);
          // For hub events, run the returned thunk regardless of tagging
          void Promise.resolve(
            thunk(store.dispatch, store.getState as () => S, connection.invoke.bind(connection)),
          ).catch((err) => {
            console.error(`SignalR handler for "${name}" failed:`, err);
          });
        } catch (err) {
          console.error(`SignalR callback for "${name}" threw:`, err);
        }
      });
    }

    ensureLifecycle(connection, onStart, reportStatus);

    queueMicrotask(() => {
      if (connectionHealth) {
        store.dispatch(registerConnection(connectionHealth) as UnknownAction);
      }

      if (shouldConnectionStartImmediately) {
        void connect(connection, onStart, reportStatus);
      }
    });

    return (next) => (action) => {
      if (isSignalThunk(action)) {
        try {
          return action(store.dispatch, store.getState as () => S, connection.invoke.bind(connection));
        } catch (err) {
          console.error("SignalR thunk execution failed:", err);
          return undefined as unknown as D;
        }
      } else if (isFunction(action)) {
        try {
          action();
        } catch (err) {
          console.error("Non-signalR thunk execution failed:", err);
        }
      }
      return next(action as D);
    };
  };

function ensureLifecycle(connection: HubConnection, onStart?: () => void, reportStatus?: StatusReporter) {
  if (lifecycleInstalled.has(connection)) return;
  lifecycleInstalled.add(connection);

  connection.onreconnecting((error) => {
    console.info("SignalR connection dropped. Attempting to reconnect.", error);
    reportStatus?.("reconnecting", error);
  });

  connection.onreconnected(() => {
    reportStatus?.("connected");
  });

  connection.onclose((error) => {
    if (error) {
      console.info("SignalR connection closed after reconnect attempts were exhausted.", error);
      reportStatus?.("disconnected", error);
    }
  });

  // Initial connection startup is still triggered by auth lifecycle events.
  document?.addEventListener("loggedIn", () => {
    void connect(connection, onStart, reportStatus);
  });

  // Stop when logging out
  document?.addEventListener("loggedOut", () => {
    if (connection.state !== HubConnectionState.Disconnected) {
      reportStatus?.("stopped");
      void connection.stop();
      return;
    }

    reportStatus?.("stopped");
  });
}

export default reduxSignalRMiddleware;
